// fallow-ignore-file boundary-violation
import type { IconName } from "@similarweb/icons";
import { Badge, EPrimaryItemType } from "@similarweb/ui-components";

import { isAvailable, isHidden } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import createCompaniesAndContactsSection from "app/components/sidebar3/dynamic-config/configs/sidebar31/factories/companiesAndContactsSection";
import createSearchAppsListItem from "app/components/sidebar3/dynamic-config/configs/sidebar31/factories/searchAppsListItem";
import { onHookClick, onSubSectionClick } from "app/components/sidebar3/SideBarItems";
import type { IPrimarySidebarItem, ISubSectionItem } from "app/components/sidebar3/types";
import { EGroupTypes, ESidebarPlacement, SecondarySideBarType3 } from "app/components/sidebar3/types";
import {
    adSearchIsOpen,
    isAdIntelligenceNavigationLocked,
    openAdIntelligenceHookModal,
} from "app/pages/digital-marketing/ad-intelligence";
import { EXTENSION_LINK, isSalesExtensionPresent, salesSettingsHelper } from "app/pages/sales-intelligence";
import { EPrimarySideBar3IdType, EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { openHooksModalV5, openHooksModalV6 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { isNoTouchOrAfterTrial, isTouchUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const isUserInTest = false; // TODO: remove this when cleanup lnx-23-download-extension-icon-update test

export const hasAppsPermission = (): boolean => swSettings.user.hasSwAppIntelligence;

const hasSalesAppIntelligencePermission = (): boolean => salesSettingsHelper.isSIAppLeadGenEnabled;
const isAiMeetingAgentEnabled = (): boolean => salesSettingsHelper.isAiMeetingAgentEnabled;
const isAiCraftEmailAgentLocked = (): boolean => salesSettingsHelper.isAiCraftEmailAgentLocked;
const isAiCraftEmailAgentHidden = (): boolean => salesSettingsHelper.isAiCraftEmailAgentHidden;
const isConsolidatedAppsEnabled = (): boolean => salesSettingsHelper.isConsolidatedAppsEnabled;

export const searchAppsNavBarSection: IPrimarySidebarItem = createCompaniesAndContactsSection([
    createSearchAppsListItem(hasSalesAppIntelligencePermission(), hasAppsPermission()),
]);

export const findCompaniesContactsNavBarSection: IPrimarySidebarItem = createCompaniesAndContactsSection([
    {
        title: "primary.sidebar.salesIntelligence.companyOverview",
        icon: "sidebar3-company-overview",
        state: EPrimarySideBar3StateType.SICompanyAnalysisHome,
        id: EPrimarySideBar3IdType.SICompanyAnalysis,
        itemType: EPrimaryItemType.GroupItem,
        onClick: (state) => {
            allTrackers.dispatchTrackEvent("SI navigation bar", "click", "company overview/prepare for a meeting");
            void proNavigator.go(state);
        },
        secondaryBarType3: SecondarySideBarType3.SICompanyOverview,
    },
    {
        title: "primary.sidebar.salesIntelligence.company.searchByFilters",
        icon: "sidebar3-search",
        state: EPrimarySideBar3StateType.SISearch,
        ParentTrackName: "Find Companies Contacts",
        id: EPrimarySideBar3IdType.SISearch,
        itemType: EPrimaryItemType.GroupItem,
        upsellBadgeComponent: isConsolidatedAppsEnabled() ? <Badge type="new" /> : undefined,
        onClick: (state) => {
            allTrackers.dispatchTrackEvent(
                "SI navigation bar",
                "click",
                "search by filters/find companies and contacts",
            );
            void proNavigator.go(state);
        },
    },
    createSearchAppsListItem(hasSalesAppIntelligencePermission(), hasAppsPermission()),
]);

export const contactsNavBarSection: IPrimarySidebarItem = {
    title: "primary.sidebar.salesIntelligence.contacts.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    placement: ESidebarPlacement.Top,
    groupedSubSection: [
        {
            title: "primary.sidebar.salesIntelligence.employeeProfiles",
            icon: "users",
            state: EPrimarySideBar3StateType.SIEmployeeProfiles,
            id: EPrimarySideBar3IdType.SIEmployeeProfiles,
            ParentTrackName: "Contacts",
            itemType: EPrimaryItemType.GroupItem,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "employee profiles/contacts");
                proNavigator.go(state);
            },
            upsellBadgeComponent: <Badge type="new" />,
        },
        {
            title: "primary.sidebar.salesIntelligence.contactsWaterfall",
            icon: "all-platforms",
            state: EPrimarySideBar3StateType.SIContactsWaterfall,
            id: EPrimarySideBar3IdType.SIContactsWaterfall,
            // Tracking stays on the pre-move label so the Waterfall funnel keeps reporting as it does today.
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "integrations/Contact waterfall");
                proNavigator.go(state);
            },
            isHidden: !isTouchUser(swSettings),
            upsellBadgeComponent: <Badge type="new" />,
        },
    ] as ISubSectionItem[],
};

export const companyNavBarSection: IPrimarySidebarItem = {
    title: "primary.sidebar.salesIntelligence.company.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    placement: ESidebarPlacement.Top,
    groupedSubSection: [
        {
            title: "primary.sidebar.salesIntelligence.email",
            icon: "ai-email",
            state: EPrimarySideBar3StateType.SIAIAgentEmail,
            id: EPrimarySideBar3IdType.SIAIAgentEmail,
            itemType: EPrimaryItemType.GroupItem,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "craft email agent");
                void proNavigator.go(state);
            },
            isHidden: isAiCraftEmailAgentHidden(),
            onClickLocked: () => {
                onHookClick("SalesSolutionEmailAgent", EPrimarySideBar3StateType.SIHome);
            },
            isLocked: isAiCraftEmailAgentLocked(),
        },
        {
            title: "primary.sidebar.salesIntelligence.meeting",
            icon: "ai-calendar",
            state: EPrimarySideBar3StateType.SIAIAgentMeetingHome,
            id: EPrimarySideBar3IdType.SIAIAgentMeetingHome,
            itemType: EPrimaryItemType.GroupItem,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent(
                    "SI navigation bar",
                    "click",
                    "meeting prep agent/prepare for a meeting",
                );
                void proNavigator.go(state, { domain: null });
            },
            isHidden: !isAiMeetingAgentEnabled(),
        },
    ] as ISubSectionItem[],
};

export const listsNavBarSection: IPrimarySidebarItem = {
    title: "primary.sidebar.salesIntelligence.monitor.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    placement: ESidebarPlacement.Top,
    groupedSubSection: [
        {
            title: "primary.sidebar.salesIntelligence.myLists",
            icon: "sidebar3-my-lists",
            state: EPrimarySideBar3StateType.SIMyList,
            ParentTrackName: "Qualify And Monitor",
            id: EPrimarySideBar3IdType.SIMyList,
            itemType: EPrimaryItemType.GroupItem,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "my lists/qualify and monitor");
                proNavigator.go(state);
            },
        },
        {
            title: "primary.sidebar.salesIntelligence.myCRMLists",
            icon: "sidebar3-my-crm-lists",
            state: EPrimarySideBar3StateType.SICRMLists,
            id: EPrimarySideBar3IdType.SICRMLists,
            ParentTrackName: "Qualify And Monitor",
            itemType: EPrimaryItemType.GroupItem,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "my crm lists/qualify and monitor");
                proNavigator.go(state);
            },
        },
    ] as ISubSectionItem[],
};

export const toolsAndManagementNavBarSection: IPrimarySidebarItem = {
    title: "primary.sidebar.salesIntelligence.toolsAndManagement.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    placement: ESidebarPlacement.Top,
    groupedSubSection: [
        {
            title: "primary.sidebar.salesIntelligence.myTeams",
            icon: "stakeholders",
            state: EPrimarySideBar3StateType.SITeamsManagement,
            id: EPrimarySideBar3IdType.SITeams,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            isHidden: !salesSettingsHelper.isHighLevelAccount(),
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "my teams/tools and management");
                proNavigator.go(state);
            },
            upsellBadgeComponent: <Badge type="new" />,
        },
        {
            title: "primary.sidebar.salesIntelligence.accountSignalsManagement",
            icon: "alerts",
            state: EPrimarySideBar3StateType.SIAccountSignalsManagement,
            id: EPrimarySideBar3IdType.SIAccountSignalsManagement,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "intent management/tools and management");
                proNavigator.go(state);
            },
        },
        {
            title: "primary.sidebar.salesIntelligence.leadScoring",
            icon: "sidebar3-lead-scoring",
            state: EPrimarySideBar3StateType.SIScoreSettings,
            id: EPrimarySideBar3IdType.SIScoreSettings,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            upsellBadgeComponent: <Badge type="new" />,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "lead scoring/tools and management");
                proNavigator.go(state);
            },
        },
        {
            title: "primary.sidebar.salesIntelligence.playbooks",
            icon: "playbooks",
            state: EPrimarySideBar3StateType.SIPlaybooks,
            id: EPrimarySideBar3IdType.SIPlaybooks,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            isHidden: !salesSettingsHelper.isSalesPlaybooksEnabled,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "playbooks/tools and management");
                proNavigator.go(state);
            },
            upsellBadgeComponent: <Badge type="new" />,
        },
        {
            title: "primary.sidebar.salesIntelligence.enrichment",
            icon: "sidebar3-enrichment",
            state: EPrimarySideBar3StateType.SIEnrichment,
            id: EPrimarySideBar3IdType.SIEnrichment,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "enrichment/tools and management");
                proNavigator.go(state);
            },
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
        },
        {
            title: "primary.sidebar.salesIntelligence.integrations",
            icon: "sidebar3-integrations",
            state: EPrimarySideBar3StateType.SIManageIntegrations,
            id: EPrimarySideBar3IdType.SIManageIntegrations,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "integrations/Management");
                proNavigator.go(state);
            },
            children: [
                {
                    title: "primary.sidebar.salesIntelligence.crmAndConnectors",
                    state: EPrimarySideBar3StateType.SIManageCRM,
                    id: EPrimarySideBar3IdType.SIManageCRM,
                    ParentTrackName: "Tools And Management",
                    itemType: EPrimaryItemType.GroupItem,
                    onClick(state) {
                        allTrackers.dispatchTrackEvent("SI navigation bar", "click", "integrations/CRM & Connectors");
                        proNavigator.go(state);
                    },
                },
                {
                    title: "primary.sidebar.salesIntelligence.apisAndWebhooks",
                    state: EPrimarySideBar3StateType.SIManageAPIsAndWebhooks,
                    id: EPrimarySideBar3IdType.SIManageAPIsAndWebhooks,
                    ParentTrackName: "Tools And Management",
                    itemType: EPrimaryItemType.GroupItem,
                    onClick(state) {
                        allTrackers.dispatchTrackEvent("SI navigation bar", "click", "integrations/APIs & Webhooks");
                        proNavigator.go(state);
                    },
                },
                {
                    title: "primary.sidebar.salesIntelligence.manageMCPs",
                    state: EPrimarySideBar3StateType.SIManageMCPs,
                    id: EPrimarySideBar3IdType.SIManageMCPs,
                    ParentTrackName: "Tools And Management",
                    itemType: EPrimaryItemType.GroupItem,
                    onClick(state) {
                        allTrackers.dispatchTrackEvent("SI navigation bar", "click", "integrations/MCPs");
                        proNavigator.go(state);
                    },
                },
            ],
        },
        {
            title: isUserInTest
                ? "primary.sidebar.salesIntelligence.getExtension"
                : "primary.sidebar.salesIntelligence.downloadExtension",
            icon: isUserInTest ? "download" : "sidebar3-extension",
            state: EPrimarySideBar3StateType.SIDownloadExtension,
            id: EPrimarySideBar3IdType.SIDownloadExtension,
            ParentTrackName: "Tools And Management",
            itemType: EPrimaryItemType.GroupItem,
            isHidden: isSalesExtensionPresent(),
            onClick(state) {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "Download extension/tools and management");
                window.open(EXTENSION_LINK, "_blank");
            },
            secondaryBarType3: SecondarySideBarType3.IndustryResearch,
            badgeData: isUserInTest ? { badgeType: "number", badgeText: "popular" } : null,
        },
    ] as ISubSectionItem[],
};

/**
 * Opens the Monitor & Protect upsell hook modal.
 * Mirrors the key/version selection used by the WI Monitor & Protect homepage header.
 */
const openMonitorAndProtectHookModal = (): void => {
    if (isNoTouchOrAfterTrial(swSettings)) {
        openHooksModalV6("monitorAndProtectNoTouch");
        return;
    }
    openHooksModalV5("MonitorAndProtectUnlock");
};

/**
 * SI Ad Intelligence items are always visible: rendered normally when the user is
 * entitled, or in trial (locked + crown + hook modal) state otherwise. The trial
 * conditions mirror the previous "hidden/locked" gating of each item.
 */
const isSalesAdvertiserActivityTrial = (): boolean =>
    !adSearchIsOpen() && (isAdIntelligenceNavigationLocked() || isHidden(swSettings.components.AdIntMaster));

const isSalesPublisherAnalysisTrial = (): boolean =>
    isAdIntelligenceNavigationLocked() || isHidden(swSettings.components.AdIntMaster);

const isSalesMonitorAndProtectTrial = (): boolean => !isAvailable(swSettings.components.MonitorAndProtectMaster);

export const salesAdIntelligenceNavBarSection: IPrimarySidebarItem = {
    title: "primary.sidebar.ad.intelligence.group.title",
    itemType: EGroupTypes.GroupPrimaryLevel,
    placement: ESidebarPlacement.Top,
    groupedSubSection: [
        {
            title: "primary.sidebar.advertiser.activity.title",
            icon: "ad-research",
            state: EPrimarySideBar3StateType.SISalesAdvertiserActivity,
            ParentTrackName: "Advertiser Activity",
            id: EPrimarySideBar3IdType.SISalesAdvertiserActivity,
            isLocked: isSalesAdvertiserActivityTrial(),
            isHidden: false,
            onClickLocked: () => {
                openAdIntelligenceHookModal();
            },
            itemType: EPrimaryItemType.GroupItem,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "advertiser activity/ad intelligence");
                onSubSectionClick(state as EPrimarySideBar3StateType);
            },
            secondaryBarType3: SecondarySideBarType3.SalesAdvertiserActivity,
        },
        {
            title: "primary.sidebar.publisher.analysis.title",
            icon: "publish",
            state: EPrimarySideBar3StateType.SISalesPublisherAnalysis,
            ParentTrackName: "Publisher Analysis",
            id: EPrimarySideBar3IdType.SISalesPublisherAnalysis,
            isLocked: isSalesPublisherAnalysisTrial(),
            isHidden: false,
            onClickLocked: () => {
                openAdIntelligenceHookModal("publisher");
            },
            itemType: EPrimaryItemType.GroupItem,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "publisher analysis/ad intelligence");
                onSubSectionClick(state as EPrimarySideBar3StateType);
            },
            secondaryBarType3: SecondarySideBarType3.SalesPublisherAnalysis,
        },
        {
            title: "primary.sidebar.monitor.protect.title",
            icon: "sidebar3-brand-protection",
            state: EPrimarySideBar3StateType.SISalesMonitorAndProtect,
            ParentTrackName: "MonitorAndProtect",
            id: EPrimarySideBar3IdType.SISalesMonitorAndProtect,
            isLocked: isSalesMonitorAndProtectTrial(),
            isHidden: false,
            onClickLocked: () => openMonitorAndProtectHookModal(),
            itemType: EPrimaryItemType.GroupItem,
            onClick: (state) => {
                allTrackers.dispatchTrackEvent("SI navigation bar", "click", "monitor and protect/ad intelligence");
                onSubSectionClick(state as EPrimarySideBar3StateType);
            },
            secondaryBarType3: SecondarySideBarType3.SalesMonitorAndProtect,
        },
    ] as ISubSectionItem[],
};

export const salesHomeNavBarItem: IPrimarySidebarItem = {
    itemType: EPrimaryItemType.StandalonePrimary,
    id: EPrimarySideBar3IdType.SIHome,
    title: "si.menu.item.home",
    icon: "sidebar3-si-home" as IconName,
    trackName: "SIHome",
    state: EPrimarySideBar3StateType.SIHome,
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Top,
    onClick: (state) => {
        allTrackers.dispatchTrackEvent("SI navigation bar", "click", "home/SI home page");
        proNavigator.go(state);
    },
};
